import { 
  Member, 
  Contribution, 
  Loan, 
  LoanPayment, 
  Distribution, 
  MemberDistribution,
  DashboardStats,
  MonthlyFinancials 
} from '../types'

// Thai names data
const THAI_FIRST_NAMES = [
  'สมชาย', 'สมหญิง', 'วิชัย', 'วิภา', 'ประยุทธ์', 'ประภา', 'สุรชัย', 'สุภา',
  'นิรันดร์', 'นิรชา', 'อานนท์', 'อัมพร', 'สมบัติ', 'สมใจ', 'วิทยา', 'วินิดา',
  'ธนา', 'ธิดา', 'กิตติ', 'กานต์', 'จิรายุ', 'จิราภา', 'ปิยะ', 'ปิยดา',
  'เอกชัย', 'เอกรินทร์', 'มานะ', 'มาลี', 'ชัยยา', 'ชลิดา', 'รักษ์', 'รัตนา',
  'อิศราพร', 'อิสรีย์', 'บัณฑิต', 'บุษรา', 'ศักดิ์', 'ศิริ', 'ไกรสร', 'ไกรลาศ',
  'ทัศน์', 'ทัศนีย์', 'รุ่งโรจน์', 'รุ่งนภา', 'สุกัญญา', 'สุกานดา', 'พงษ์', 'พรรณ',
  'ชาตรี', 'ชาติกา', 'มงคล', 'มณีรัตน์', 'วรเศรษฐ์', 'วราภรณ์', 'อรรถกร', 'อรอนงค์'
]

const THAI_LAST_NAMES = [
  'รุ่งเรือง', 'วงศ์ใหญ่', 'ศรีสุข', 'มั่นคง', 'เจริญสุข', 'ธนาคาร', 'สินธุ์',
  'อินทร์แก้ว', 'ทองคำ', 'เพชรดี', 'มณีวงศ์', 'รัตนไพบูลย์', 'ศรีสวัสดิ์',
  'ปัญญาวงศ์', 'วิญญูวงศ์', 'เมืองใจ', 'สุขสบาย', 'ปราณีต', 'วิเศษ',
  'สุวรรณ', 'ทรัพย์มา', 'บุญเลิศ', 'ดีมาก', 'สมบูรณ์', 'เลิศล้ำ',
  'พันธุ์ดี', 'จิตรใส', 'อำนวย', 'สุดใส', 'ทองดี', 'ขำคม', 'เจริญ',
  'แสงใส', 'ใสใจ', 'สดใส', 'ปลื้มใจ', 'สุขใจ', 'ชื่นใจ', 'ใจดี'
]

// Generate a random Thai phone number
function generateThaiPhone(): string {
  const prefixes = ['081', '082', '083', '084', '085', '086', '087', '088', '089', '061', '062', '063', '064', '065', '066', '067', '068', '069']
  const prefix = prefixes[Math.floor(Math.random() * prefixes.length)]
  const number = Math.floor(Math.random() * 10000000).toString().padStart(7, '0')
  return `${prefix}-${number.slice(0, 3)}-${number.slice(3)}`
}

// Generate a random date within a range
function randomDate(start: Date, end: Date): string {
  const date = new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()))
  return date.toISOString().split('T')[0]
}

// Generate a random Buddhist year date (Thai calendar) - currently unused but available for future use
// function randomBuddhistDate(start: Date, end: Date): string {
//   const gregorianDate = randomDate(start, end)
//   const date = new Date(gregorianDate)
//   const buddhistYear = date.getFullYear() + 543
//   return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${buddhistYear}`
// }

// Mock data class
export class MockDataGenerator {
  private seed: number
  private members: Member[] = []
  private contributions: Contribution[] = []
  private loans: Loan[] = []
  private loanPayments: LoanPayment[] = []
  private distributions: Distribution[] = []
  private memberDistributions: MemberDistribution[] = []

  constructor(seed: number = 12345) {
    this.seed = seed
    this.initializeData()
  }

  private random(): number {
    this.seed = (this.seed * 9301 + 49297) % 233280
    return this.seed / 233280
  }

  private randomInt(min: number, max: number): number {
    return Math.floor(this.random() * (max - min + 1)) + min
  }

  private randomChoice<T>(array: T[]): T {
    return array[Math.floor(this.random() * array.length)]
  }

  private generateMemberId(): string {
    return `MEM${this.randomInt(100, 999).toString().padStart(3, '0')}`
  }

  private generateContributionId(): string {
    return `CON${this.randomInt(1000, 9999)}`
  }

  private generateLoanId(): string {
    return `LON${this.randomInt(1000, 9999)}`
  }

  private generatePaymentId(): string {
    return `PAY${this.randomInt(10000, 99999)}`
  }

  private generateDistributionId(): string {
    return `DIS${this.randomInt(1000, 9999)}`
  }

  private initializeData(): void {
    // Generate 85-100 members
    const memberCount = this.randomInt(85, 100)
    
    for (let i = 0; i < memberCount; i++) {
      this.members.push(this.generateMember())
    }

    // Generate contribution history for the last 24 months
    this.generateContributionHistory()

    // Generate some active loans
    this.generateLoans()

    // Generate loan payments
    this.generateLoanPayments()

    // Generate past distributions
    this.generateDistributions()
  }

  private generateMember(): Member {
    const firstName = this.randomChoice(THAI_FIRST_NAMES)
    const lastName = this.randomChoice(THAI_LAST_NAMES)
    const memberId = this.generateMemberId()
    const joinDate = randomDate(new Date('2020-01-01'), new Date('2024-01-01'))
    
    // Some members might have LINE ID or Facebook ID
    const hasLineId = this.random() > 0.3
    const hasFacebookId = this.random() > 0.4
    const hasEmail = this.random() > 0.5
    const hasPhone = this.random() > 0.1 // Most people have phones

    // Role distribution: mostly members, some board members, one treasurer
    let role: 'member' | 'treasurer' | 'board' = 'member'
    const roleRand = this.random()
    if (roleRand > 0.95) role = 'treasurer'
    else if (roleRand > 0.85) role = 'board'

    // Status: most active, some inactive
    const status: 'active' | 'inactive' = this.random() > 0.05 ? 'active' : 'inactive'

    return {
      memberId,
      name: `${firstName} ${lastName}`,
      lineId: hasLineId ? `${firstName.toLowerCase()}${this.randomInt(10, 99)}` : undefined,
      facebookId: hasFacebookId ? `${firstName}.${lastName}${this.randomInt(1, 9)}` : undefined,
      email: hasEmail ? `${firstName.toLowerCase()}.${lastName.toLowerCase()}@gmail.com` : undefined,
      phoneNumber: hasPhone ? generateThaiPhone() : undefined,
      role,
      status,
      joinDate,
      createdAt: joinDate,
      updatedAt: joinDate
    }
  }

  private generateContributionHistory(): void {
    const currentDate = new Date()
    
    // Generate contributions for the last 24 months
    for (let monthsBack = 0; monthsBack < 24; monthsBack++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - monthsBack, 1)
      const year = date.getFullYear()
      const month = date.getMonth() + 1
      
      // Not all members contribute every month
      const activeMembers = this.members.filter(m => m.status === 'active')
      const contributingMembers = activeMembers.filter(() => this.random() > 0.1) // 90% contribution rate
      
      contributingMembers.forEach(member => {
        // Standard contribution is 500 THB, but can vary
        const baseAmount = 500
        const variation = this.randomInt(-50, 100)
        const amount = baseAmount + variation
        
        const paymentMethod = this.randomChoice(['cash', 'transfer', 'line_pay'] as const)
        const status = this.random() > 0.05 ? 'confirmed' : 'pending' // 95% confirmed
        
        const paymentDate = randomDate(
          new Date(year, month - 1, 1),
          new Date(year, month - 1, 28)
        )

        this.contributions.push({
          contributionId: this.generateContributionId(),
          memberId: member.memberId,
          amount,
          year,
          month,
          paymentDate,
          paymentMethod,
          status,
          confirmedBy: status === 'confirmed' ? 'SYSTEM' : undefined,
          confirmedAt: status === 'confirmed' ? paymentDate : undefined,
          createdAt: paymentDate,
          updatedAt: paymentDate
        })
      })
    }
  }

  private generateLoans(): void {
    const activeMembers = this.members.filter(m => m.status === 'active')
    const loanCount = this.randomInt(8, 15) // 8-15 active loans
    
    for (let i = 0; i < loanCount; i++) {
      const member = this.randomChoice(activeMembers)
      const amount = this.randomInt(5000, 100000) // 5K - 100K THB
      const profitRate = this.random() > 0.5 ? 2.0 : 1.5 // 1.5% or 2% monthly
      const termMonths = this.randomChoice([6, 12, 18, 24, 36])
      const startDate = randomDate(new Date('2023-01-01'), new Date())
      
      const startDateObj = new Date(startDate)
      const endDate = new Date(startDateObj.getFullYear(), startDateObj.getMonth() + termMonths, startDateObj.getDate())
      
      const monthlyPayment = Math.round((amount * (1 + (profitRate / 100) * termMonths)) / termMonths)
      
      // Calculate remaining balance based on how many payments have been made
      const monthsElapsed = Math.floor((Date.now() - startDateObj.getTime()) / (1000 * 60 * 60 * 24 * 30))
      const paymentsMade = Math.min(monthsElapsed, termMonths)
      const remainingBalance = Math.max(0, amount + (amount * (profitRate / 100) * termMonths) - (monthlyPayment * paymentsMade))
      
      let status: 'active' | 'pending' | 'approved' | 'paid' | 'overdue' | 'rejected' = 'active'
      if (remainingBalance === 0) status = 'paid'
      else if (endDate < new Date()) {
        status = this.random() > 0.2 ? 'overdue' : 'rejected'
      }

      this.loans.push({
        loanId: this.generateLoanId(),
        memberId: member.memberId,
        amount,
        profitRate,
        startDate,
        endDate: endDate.toISOString().split('T')[0],
        monthlyPayment,
        remainingBalance,
        status,
        purpose: this.randomChoice(['เพื่อการศึกษา', 'เพื่อการรักษาพยาบาล', 'เพื่อธุรกิจ', 'เพื่อที่อยู่อาศัย', 'เพื่อการเกษตร']),
        approvedBy: 'BOARD001',
        approvedAt: startDate,
        createdAt: startDate,
        updatedAt: startDate
      })
    }
  }

  private generateLoanPayments(): void {
    this.loans.forEach(loan => {
      const startDate = new Date(loan.startDate)
      const currentDate = new Date()
      const monthsElapsed = Math.floor((currentDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 30))
      
      // Generate payments for each month elapsed
      for (let month = 0; month < monthsElapsed && loan.remainingBalance > 0; month++) {
        const paymentDate = new Date(startDate.getFullYear(), startDate.getMonth() + month, startDate.getDate())
        
        // Not all payments are on time
        if (this.random() > 0.1) { // 90% payment rate
          const principalAmount = Math.round(loan.amount / (loan.endDate ? 
            Math.ceil((new Date(loan.endDate).getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 30)) : 12))
          const interestAmount = loan.monthlyPayment - principalAmount
          
          this.loanPayments.push({
            paymentId: this.generatePaymentId(),
            loanId: loan.loanId,
            amount: loan.monthlyPayment,
            principalAmount,
            interestAmount,
            paymentDate: paymentDate.toISOString().split('T')[0],
            paymentMethod: this.randomChoice(['cash', 'transfer', 'line_pay'] as const),
            createdAt: paymentDate.toISOString().split('T')[0],
            updatedAt: paymentDate.toISOString().split('T')[0]
          })
        }
      }
    })
  }

  private generateDistributions(): void {
    // Generate distributions for the last 3 years
    const currentYear = new Date().getFullYear()
    
    for (let year = currentYear - 3; year < currentYear; year++) {
      const totalContributions = this.contributions
        .filter(c => c.year === year && c.status === 'confirmed')
        .reduce((sum, c) => sum + c.amount, 0)
      
      const expenses = this.randomInt(50000, 100000) // Operating expenses
      const profitAmount = Math.max(0, totalContributions - expenses)
      const memberCount = this.members.filter(m => m.status === 'active').length
      const perMemberAmount = Math.floor(profitAmount / memberCount)
      
      if (profitAmount > 0) {
        const distributionId = this.generateDistributionId()
        
        this.distributions.push({
          distributionId,
          year,
          totalAmount: profitAmount,
          profitAmount,
          memberCount,
          perMemberAmount,
          status: 'distributed',
          approvedBy: 'BOARD001',
          approvedAt: `${year + 1}-01-15`,
          distributedAt: `${year + 1}-02-01`,
          createdAt: `${year + 1}-01-10`,
          updatedAt: `${year + 1}-02-01`
        })

        // Generate member distributions
        this.members.filter(m => m.status === 'active').forEach(member => {
          const eligibleMonths = this.contributions
            .filter(c => c.year === year && c.memberId === member.memberId && c.status === 'confirmed')
            .length
          
          const amount = Math.floor((perMemberAmount * eligibleMonths) / 12)
          
          this.memberDistributions.push({
            memberDistributionId: `MD${this.randomInt(10000, 99999)}`,
            distributionId,
            memberId: member.memberId,
            eligibleMonths,
            amount,
            paymentMethod: this.randomChoice(['cash', 'transfer'] as const),
            paymentStatus: 'paid',
            paymentDate: `${year + 1}-02-01`,
            createdAt: `${year + 1}-01-15`,
            updatedAt: `${year + 1}-02-01`
          })
        })
      }
    }
  }

  // Public methods to get mock data
  getMembers(): Member[] {
    return [...this.members]
  }

  getMember(memberId: string): Member | undefined {
    return this.members.find(m => m.memberId === memberId)
  }

  getContributions(): Contribution[] {
    return [...this.contributions]
  }

  getContributionsByMember(memberId: string): Contribution[] {
    return this.contributions.filter(c => c.memberId === memberId)
  }

  getLoans(): Loan[] {
    return [...this.loans]
  }

  getLoansByMember(memberId: string): Loan[] {
    return this.loans.filter(l => l.memberId === memberId)
  }

  getLoanPayments(): LoanPayment[] {
    return [...this.loanPayments]
  }

  getLoanPaymentsByLoan(loanId: string): LoanPayment[] {
    return this.loanPayments.filter(p => p.loanId === loanId)
  }

  getDistributions(): Distribution[] {
    return [...this.distributions]
  }

  getMemberDistributions(): MemberDistribution[] {
    return [...this.memberDistributions]
  }

  getDashboardStats(): DashboardStats {
    const activeMembers = this.members.filter(m => m.status === 'active')
    
    const totalContributions = this.contributions
      .filter(c => c.status === 'confirmed')
      .reduce((sum, c) => sum + c.amount, 0)
    
    const totalLoans = this.loans
      .filter(l => l.status === 'active')
      .reduce((sum, l) => sum + l.remainingBalance, 0)
    
    const pendingContributions = this.contributions
      .filter(c => c.status === 'pending')
      .reduce((sum, c) => sum + c.amount, 0)
    
    const overdueLoans = this.loans.filter(l => l.status === 'overdue' || l.status === 'rejected').length

    return {
      totalMembers: this.members.length,
      activeMembers: activeMembers.length,
      totalContributions,
      totalLoans,
      availableFunds: Math.max(0, totalContributions - totalLoans),
      pendingContributions,
      overdueLoans,
      monthlyContributionTarget: activeMembers.length * 500 // 500 THB per member
    }
  }

  getMonthlyFinancials(year: number, month: number): MonthlyFinancials {
    const contributions = this.contributions
      .filter(c => c.year === year && c.month === month && c.status === 'confirmed')
      .reduce((sum, c) => sum + c.amount, 0)
    
    const loanPayments = this.loanPayments
      .filter(p => {
        const date = new Date(p.paymentDate)
        return date.getFullYear() === year && date.getMonth() + 1 === month
      })
      .reduce((sum, p) => sum + p.amount, 0)
    
    const newLoans = this.loans
      .filter(l => {
        const date = new Date(l.startDate)
        return date.getFullYear() === year && date.getMonth() + 1 === month
      })
      .reduce((sum, l) => sum + l.amount, 0)
    
    const expenses = this.randomInt(5000, 15000) // Monthly operating expenses
    
    return {
      year,
      month,
      contributions,
      loanPayments,
      newLoans,
      expenses,
      netIncome: contributions + loanPayments - newLoans - expenses
    }
  }
}

// Singleton instance
export const mockData = new MockDataGenerator()